import { CRAWL_ITEM_KINDS, NotFoundError } from '@zyx/shared';
import type { CrawlItemKind, CrawlItemStatus, Job, JobItem } from '@zyx/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { ErrorState, LoadingRows } from '@/components/common/states';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useApi } from '@/hooks/use-connection';
import { describeTries, formatIn, ITEM_KIND_LABELS, ITEM_STATUS_LABELS } from '@/lib/crawl-labels';
import { formatAgo } from '@/lib/format';
import { ItemLabel } from './item-label';
import { Outcome, seconds } from './item-dialog';

const PAGE_SIZE = 50;
const POLL_MS = 4_000;

/** Tabs in the order work moves through them; `cancelled` only when there is some. */
const TABS: ReadonlyArray<CrawlItemStatus> = ['pending', 'claimed', 'failed', 'dead', 'done', 'cancelled'];

/** The tab a job's page opens on: where there is work to watch, else what got done. */
function defaultTab(job: Job): CrawlItemStatus {
  return (['pending', 'failed', 'dead'] as const).find((status) => job.progress[status] > 0) ?? 'done';
}

const ALL_KINDS = 'all';

/** The job exists (its page loaded), so a 404 here means a service from before these lists. */
function unsupported(error: unknown): boolean {
  return error instanceof NotFoundError;
}

function Unsupported(props: { readonly title: string }) {
  return (
    <section className="space-y-1">
      <h2 className="text-lg font-semibold tracking-tight">{props.title}</h2>
      <p className="text-sm text-muted-foreground">The zyx service you are connected to is too old to list these. Update it to see them.</p>
    </section>
  );
}

/** When it last moved: finished, retrying soon, or queued. */
function when(item: JobItem): string {
  if (item.status === 'failed' && item.nextAttemptAt !== undefined) {
    return `retry ${formatIn(item.nextAttemptAt)}`;
  }
  if (item.lastFinishedAt !== undefined && item.status !== 'pending') {
    return formatAgo(item.lastFinishedAt) ?? '';
  }
  return `queued ${formatAgo(item.createdAt) ?? ''}`;
}

/** Every item the job owns, a status at a time, filterable by kind and paged. */
export function JobItems(props: { readonly job: Job; readonly onSelect: (itemId: number) => void }) {
  const { job } = props;
  const api = useApi();
  // Chosen once: recomputed on every poll, it would jump between Waiting and Done each
  // time the only waiting item starts running.
  const [status, setStatus] = useState<CrawlItemStatus>(() => defaultTab(job));
  const [kind, setKind] = useState<CrawlItemKind | typeof ALL_KINDS>(ALL_KINDS);
  const [page, setPage] = useState(0);
  const query = useQuery({
    queryKey: ['jobs', 'items', job.jobId, status, kind, page],
    queryFn: () => api.listJobItems({ jobId: job.jobId, status, kind: kind === ALL_KINDS ? undefined : kind, limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
    refetchInterval: (current) => (unsupported(current.state.error) ? false : POLL_MS),
    retry: (count, error) => !unsupported(error) && count < 3,
    placeholderData: keepPreviousData,
  });
  if (unsupported(query.error)) {
    return <Unsupported title="Items" />;
  }
  const tabs = TABS.filter((candidate) => candidate !== 'cancelled' || job.progress.cancelled > 0);
  const total = query.data?.total ?? 0;
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold tracking-tight">Items</h2>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs
          value={status}
          onValueChange={(value) => {
            setStatus(TABS.find((candidate) => candidate === value) ?? status);
            setKind(ALL_KINDS);
            setPage(0);
          }}
        >
          <TabsList className="h-auto flex-wrap">
            {tabs.map((candidate) => (
              <TabsTrigger key={candidate} value={candidate}>
                {ITEM_STATUS_LABELS[candidate]} <span className="ml-1 tabular-nums text-muted-foreground">{job.progress[candidate].toLocaleString()}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Select
          value={kind}
          onValueChange={(value) => {
            setKind(CRAWL_ITEM_KINDS.find((candidate) => candidate === value) ?? ALL_KINDS);
            setPage(0);
          }}
        >
          <SelectTrigger className="w-48" aria-label="Kind of item">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_KINDS}>All kinds</SelectItem>
            {CRAWL_ITEM_KINDS.filter((candidate) => (query.data?.kinds[candidate] ?? 0) > 0 || candidate === kind).map((candidate) => (
              <SelectItem key={candidate} value={candidate}>
                {ITEM_KIND_LABELS[candidate]} ({(query.data?.kinds[candidate] ?? 0).toLocaleString()})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {query.isPending ? (
        <LoadingRows rows={4} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : query.data.items.length === 0 && page > 0 ? (
        // Its items moved on to another status while it was open.
        <p className="text-sm text-muted-foreground">
          Nothing is left on this page.{' '}
          <button type="button" className="text-primary hover:underline" onClick={() => setPage(Math.min(page - 1, lastPage))}>
            Go back
          </button>
        </p>
      ) : query.data.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No {ITEM_STATUS_LABELS[status].toLowerCase()} items{kind === ALL_KINDS ? '' : ` of this kind`}.
        </p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Tries</TableHead>
                  <TableHead>When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {query.data.items.map((item) => (
                  <TableRow key={item.itemId} className="cursor-pointer" onClick={() => props.onSelect(item.itemId)}>
                    <TableCell className="max-w-xl text-xs">
                      <ItemLabel kind={item.kind} target={item.target} subject={item.subject} />
                      {item.lastError === undefined || item.status === 'done' ? null : <p className="mt-0.5 truncate text-[11px] text-amber-700 dark:text-amber-400">{item.lastError}</p>}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{describeTries(item)}</TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{when(item)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground">
            <span className="tabular-nums">
              {(page * PAGE_SIZE + 1).toLocaleString()}–{Math.min(total, (page + 1) * PAGE_SIZE).toLocaleString()} of {total.toLocaleString()}
            </span>
            <Button variant="outline" size="icon" aria-label="Previous page" disabled={page === 0} onClick={() => setPage(page - 1)}>
              <ChevronLeft className="size-4" />
            </Button>
            <Button variant="outline" size="icon" aria-label="Next page" disabled={page >= lastPage} onClick={() => setPage(page + 1)}>
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </>
      )}
    </section>
  );
}

const RUNS_STEP = 25;
const RUNS_MAX = 500;

/** The job's latest runs, newest first, as they happen. */
export function JobRuns(props: { readonly jobId: number; readonly onSelect: (itemId: number) => void }) {
  const api = useApi();
  const [limit, setLimit] = useState(RUNS_STEP);
  const query = useQuery({
    queryKey: ['jobs', 'runs', props.jobId, limit],
    queryFn: () => api.listJobRuns({ jobId: props.jobId, limit }),
    refetchInterval: (current) => (unsupported(current.state.error) ? false : POLL_MS),
    retry: (count, error) => !unsupported(error) && count < 3,
    placeholderData: keepPreviousData,
  });
  if (unsupported(query.error)) {
    return <Unsupported title="Recent runs" />;
  }

  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold tracking-tight">Recent runs</h2>
      {query.isPending ? (
        <LoadingRows rows={4} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : query.data.runs.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing has run yet.</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Finished</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead className="text-right">Took</TableHead>
                  <TableHead className="text-right">Found</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {query.data.runs.map((run) => (
                  <TableRow key={run.attemptId} className="cursor-pointer" onClick={() => props.onSelect(run.itemId)}>
                    <TableCell className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">{new Date(run.finishedAt).toLocaleTimeString()}</TableCell>
                    <TableCell className="max-w-xl text-xs">
                      <ItemLabel kind={run.kind} target={run.target} subject={run.subject} />
                      {run.error === undefined ? null : <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{run.error}</p>}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs">
                      <Outcome outcome={run.outcome} />
                      {run.httpStatus === undefined ? null : <span className="ml-2 text-muted-foreground">HTTP {run.httpStatus}</span>}
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums text-muted-foreground">{seconds(run)}</TableCell>
                    <TableCell className="whitespace-nowrap text-right text-xs tabular-nums text-muted-foreground">
                      {run.queuedNew + run.queuedKnown === 0 ? '—' : `${run.queuedNew} new · ${run.queuedKnown} known`}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {query.data.runs.length < limit || limit >= RUNS_MAX ? null : (
            <div className="flex justify-center">
              <Button variant="outline" size="sm" onClick={() => setLimit(Math.min(RUNS_MAX, limit + RUNS_STEP * 2))}>
                Show more
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
